import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { COMMON_HELP_POPUP_BUTTON_CLASSNAME } from '../../../constants/components';
import MicButton from '../../../trc/pages/instructions/MicButton';
import { MIC_FAILURE_TYPES, MIC_FAILURE_MESSAGE, MIC_FAILURE_MESSAGE_AR } from '../../../trc/pages/instructions/TROInstructionsConstants';
import LogoutButton from '../../error/LogoutButton';
import HelpPopup from './HelpPopup';
import { redirectToAR, getARUrl } from '../../../trc/troUtils';

const TROTroubleshootingLink = '/support_center/TRO_Mic_Troubleshooting.pdf';
const TROTroubleshootingLinkAR = 'https://amplify.com/wp-content/uploads/2022/07/Placement_Mic_Troubleshooting_Amplify-Reading.pdf';

// eslint-disable-next-line consistent-return
const renderHelpMessages = (problem, onRestart) => {
    if (problem === MIC_FAILURE_TYPES.ACCESS_DENIED) {
        return (
            <div>
                <div>
                    Please follow these
                    {' '}
                    <a className="link new-tab" href={TROTroubleshootingLink} target="_blank" rel="noopener noreferrer">
                        steps to turn on the microphone.
                    </a>
                </div>
                <div>
                    Once microphone has been turned on, please
                    <br />
                    <a className="link" href="#" onClick={onRestart}>
                        restart the assessment from the beginning.
                    </a>
                </div>
            </div>
        );
    }

    if (problem === MIC_FAILURE_TYPES.ACCESS_FAILED) {
        return (
            <div>
                <div>
                    Something went wrong. Please
                    <br />
                    <a className="link" onClick={onRestart} href="#">
                        restart the assessment from the beginning.
                    </a>
                </div>
                <div>
                    For troubleshooting steps,
                    {' '}
                    <a className="link new-tab" href={TROTroubleshootingLink} rel="noopener noreferrer" target="_blank">
                        click here.
                        {' '}
                    </a>
                </div>
            </div>
        );
    }
};

const renderArHelpMessages = (problem, onRestart, trackTroError) => {
    const queryParams = {
        hasTroError: true,
        benchmarkMode: false,
    };

    const onContinueWithoutMicClick = async (event) => {
        event.preventDefault();
        await trackTroError(problem);
        window.location.href = getARUrl(queryParams);
    };

    if (problem === MIC_FAILURE_TYPES.ACCESS_DENIED) {
        return (
            <div>
                <div>
                    Follow these
                    {' '}
                    <a className="link new-tab" href={TROTroubleshootingLinkAR} target="_blank" rel="noopener noreferrer">
                        steps to turn on the microphone.
                    </a>
                </div>
                <div>
                    Once microphone has been turned on, please
                    {' '}
                    <a className="link" href="#" onClick={onRestart}>
                        restart the assessment
                    </a>
                    {' '}
                    from the beginning.
                </div>
                <div>
                    <div className="line"><span>or</span></div>
                    Continue without microphone
                    {' '}
                    <a
                        className="link"
                        href="#"
                        onClick={(e) => onContinueWithoutMicClick(e)}
                    >
                        here
                    </a>
                </div>
            </div>
        );
    }

    if (problem === MIC_FAILURE_TYPES.ACCESS_FAILED) {
        return (
            <div>
                <div>
                    Something went wrong. The assessment needs to
                    {' '}
                    <a className="link" onClick={onRestart} href="#">
                        restart from the beginning.
                    </a>
                </div>
                <div>
                    For troubleshooting tips,
                    {' '}
                    <a className="link new-tab" href={TROTroubleshootingLink} rel="noopener noreferrer" target="_blank">
                        click here.
                    </a>
                </div>
                <div>
                    <div className="line"><span>or</span></div>
                    Continue without microphone
                    {' '}
                    <a
                        className="link"
                        href="#"
                        onClick={(e) => onContinueWithoutMicClick(e)}
                    >
                        here
                    </a>
                </div>
            </div>
        );
    }
    return false;
};

// eslint-disable-next-line consistent-return
const getHelpButtons = (problem, onTestRetry, onLogout) => {
    if (problem === MIC_FAILURE_TYPES.SECOND_MIC_TEST_FAILED) {
        return [
            <MicButton ariaLabel="Microphone test" text="Test the microphone" onClick={onTestRetry} key="popup-mic-test-button" />
        ];
    }

    if (problem === MIC_FAILURE_TYPES.THIRD_MIC_TEST_FAILED || problem === MIC_FAILURE_TYPES.MIC_TEST_ERROR) {
        return [
            <LogoutButton
                key="log-out"
                className={COMMON_HELP_POPUP_BUTTON_CLASSNAME}
                onClick={onLogout}
                isSecondary
            />,
        ];
    }
};

const handlePopupOnBenchmarkMode = async (trackTroError) => {
    const queryParams = {
        hasTroError: true,
        benchmarkMode: true,
    };
    await trackTroError('Mic Help Popup: TRO Benchmark Mode enabled');
    redirectToAR(queryParams);
};

const MicHelpPopup = ({
    onRestart,
    problem,
    onTestRetry,
    onLogout,
    isExternalURLSourcePresent,
    isBenchmarkModeEnabled,
    trackTroError,
    trackFailedEnableMic,
}) => {
    useEffect(() => {
        if (!isExternalURLSourcePresent) trackFailedEnableMic();
    }, []);

    useEffect(() => {
        if (isBenchmarkModeEnabled) {
            handlePopupOnBenchmarkMode(trackTroError);
        }
    }, [isBenchmarkModeEnabled]);

    return (
        !isBenchmarkModeEnabled ? (
            <HelpPopup problem={isExternalURLSourcePresent ? MIC_FAILURE_MESSAGE_AR[problem] : MIC_FAILURE_MESSAGE[problem]} buttons={getHelpButtons(problem, onTestRetry, onLogout)} isExternalURLSourcePresent={isExternalURLSourcePresent}>
                {isExternalURLSourcePresent ? renderArHelpMessages(problem, onRestart, trackTroError) : renderHelpMessages(problem, onRestart)}
            </HelpPopup>
        ) : null);
};

MicHelpPopup.propTypes = {
    onRestart: PropTypes.func.isRequired,
    problem: PropTypes.string.isRequired,
    onTestRetry: PropTypes.func,
    onLogout: PropTypes.func,
    isExternalURLSourcePresent: PropTypes.bool.isRequired,
    isBenchmarkModeEnabled: PropTypes.bool.isRequired,
    trackTroError: PropTypes.func,
    trackFailedEnableMic: PropTypes.func,
};

MicHelpPopup.defaultProps = {
    onTestRetry: () => {},
    onLogout: () => {},
    trackTroError: () => {},
    trackFailedEnableMic: () => {},
};

export default MicHelpPopup;
